import Icon from "../../components/Icon";
import Badge from "../../components/Badge";
import type { ClientRow, SpecRow, MediaRow } from "../../types";

// Live preview pane: renders a mock of the public surface from the CURRENT
// form values and the asset's real specs/media. No external URL is fetched;
// the preview reflects unsaved edits in the editor (mirrors the v2 prototype).
type Props = {
  client: ClientRow | null;
  title: string;
  slug: string | null;
  fields: Record<string, string>;
  specs: SpecRow[];
  media: MediaRow[];
  pubState: string | null;
  previewWidth: "desktop" | "mobile";
  setPreviewWidth: (w: "desktop" | "mobile") => void;
};

const GREEN = "var(--status-published, #b8d09e)";

function firstOf(fields: Record<string, string>, keys: string[]): string {
  for (const k of keys) if (fields[k]) return fields[k];
  return "";
}

export default function PreviewPane({
  client, title, slug, fields, specs, media, pubState, previewWidth, setPreviewWidth,
}: Props) {
  const brand = (client?.name ?? "Preview").toUpperCase();
  const headline = firstOf(fields, ["catalog_title", "detail_title", "name"]) || title;
  const category = fields.category || "";
  const summary = firstOf(fields, ["detail_summary", "summary", "detail_tagline"]);
  const status = fields.status || pubState || "draft";
  const primary = media.find((m) => m.is_primary === 1) ?? media[0];
  const heroImg = primary?.image_url || "";
  const frameMax = previewWidth === "mobile" ? 420 : "100%";

  return (
    <div style={{ position: "sticky", top: 142 }}>
      <div className="preview-pane">
        <div className="preview-toolbar">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, textTransform: "uppercase", letterSpacing: "0.1em" }}>
            <span style={{ width: 6, height: 6, background: GREEN, borderRadius: 999 }} />
            live preview
          </span>
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <div className="chip-filter" style={{ padding: 2 }}>
              <button className={previewWidth === "desktop" ? "on" : ""} onClick={() => setPreviewWidth("desktop")} style={{ padding: "3px 7px", fontSize: 11 }} title="Desktop">
                <Icon name="monitor" size={12} />
              </button>
              <button className={previewWidth === "mobile" ? "on" : ""} onClick={() => setPreviewWidth("mobile")} style={{ padding: "3px 7px", fontSize: 11 }} title="Mobile">
                <Icon name="smartphone" size={12} />
              </button>
            </div>
          </span>
        </div>
        <div className="preview-toolbar">
          <span className="url" style={{ width: "100%" }}>
            <span style={{ width: 8, height: 8, background: GREEN, borderRadius: 999, display: "inline-block" }} />
            <span style={{ color: "var(--fg-muted)" }}>staging</span>
            <span style={{ color: "var(--fg)" }}>/{slug ?? "unsaved"}</span>
          </span>
        </div>
        <div className="preview-frame" style={{ maxWidth: frameMax, margin: previewWidth === "mobile" ? "0 auto" : "0" }}>
          <div className="mock-site">
            <div className="ms-nav">
              <span className="brand">{brand}</span>
              <span>Catalog · About · Contact</span>
            </div>
            <div className="ms-sub">{category || "Piece"}</div>
            <div className="ms-title">{headline || "Untitled"}</div>
            <div style={{ marginTop: 8, marginBottom: 18, display: "flex", gap: 10, alignItems: "center" }}>
              <Badge kind={status === "available" || status === "published" ? "published" : "draft"}>{status}</Badge>
            </div>
            <div className="ms-hero">
              {heroImg ? (
                <img src={heroImg} alt={headline} />
              ) : (
                <div style={{
                  width: "100%", height: "100%",
                  background: "linear-gradient(135deg, #6d5235 0%, #2a1c10 100%)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "rgba(244,196,90,0.18)", fontFamily: "var(--font-serif)", fontSize: 64, fontWeight: 700,
                }}>
                  {(headline || "·").slice(0, 1)}
                </div>
              )}
            </div>
            {summary && <div className="ms-body" style={{ marginBottom: 18 }}>{summary}</div>}
            {specs.length > 0 && (
              <div className="ms-grid">
                {specs.slice(0, 6).map((s) => (
                  <div key={s.id}><div className="k">{s.label}</div><div className="v">{s.value || "—"}</div></div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-muted)", paddingTop: 4 }}>
          <span>preview reflects unsaved edits · staging</span>
        </div>
      </div>
    </div>
  );
}
